import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Clock3, Square, ArrowRight } from 'lucide-react';

const STEP_STATE_OPTIONS = [
  { value: 'not_started', label: 'Non démarrée' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'completed', label: 'Terminée' }
];

const EXIT_TYPE_OPTIONS = [
  { value: 'demission', label: 'Démission' },
  { value: 'fin_periode_essai', label: 'Fin de période d’essai' },
  { value: 'fin_contrat', label: 'Fin de contrat' },
  { value: 'abandon_poste', label: 'Abandon de poste' },
  { value: 'licenciement', label: 'Licenciement' },
  { value: 'rupture_conventionnelle', label: 'Rupture conventionnelle' }
];

const RH_FLOW_CONFIG = {
  amazon: [
    { key: 'account_created', label: 'Compte salarié créé' },
    { key: 'account_validated', label: 'Compte salarié validé' },
    { key: 'amazon_account_request_sent', label: 'Mail de demande de création compte Amazon envoyé' },
    { key: 'amazon_account_created', label: 'Compte Amazon créé' },
    { key: 'license_verified', label: 'Permis validé' },
    { key: 'accurate_done', label: 'Accurate réalisé' },
    { key: 'safety_training_done', label: 'Formation Safety réalisée', training: true },
    { key: 'binome_training_done', label: 'Formation Binôme réalisée', training: true },
    { key: 'amazon_training_done', label: 'Formation Amazon réalisée', training: true },
    { key: 'contract_sent', label: 'Contrat envoyé' },
    { key: 'contract_signed', label: 'Contrat signé' },
    { key: 'projected_start_date', label: 'Date prévisionnelle de début' },
    { key: 'integration_completed', label: 'Premier jour effectué / Activation du salarié', aliases: ['first_day_done'] }
  ],
  colis_prive: [
    { key: 'account_created', label: 'Compte salarié créé' },
    { key: 'account_validated', label: 'Compte salarié validé' },
    { key: 'onboarding_mail_sent', label: 'Mail d’onboarding envoyé' },
    { key: 'training_call_sent', label: 'Convocation formation Binôme envoyée' },
    { key: 'binome_training_done', label: 'Formation Binôme réalisée', training: true },
    { key: 'contract_sent', label: 'Contrat envoyé' },
    { key: 'contract_signed', label: 'Contrat signé' },
    { key: 'projected_start_date', label: 'Date prévisionnelle de début' },
    { key: 'integration_completed', label: 'Premier jour effectué / Activation du salarié', aliases: ['first_day_done'] }
  ]
};

const getStatusIcon = (state) => {
  if (state === 'completed') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid="rh-step-status-icon-completed" />;
  }
  if (state === 'in_progress') {
    return <Clock3 className="h-5 w-5 text-amber-600" data-testid="rh-step-status-icon-in-progress" />;
  }
  return <Square className="h-5 w-5 text-slate-400" data-testid="rh-step-status-icon-not-started" />;
};

const normalizeRhDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const ddmmyyyy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (ddmmyyyy) {
    const day = ddmmyyyy[1].padStart(2, '0');
    const month = ddmmyyyy[2].padStart(2, '0');
    const year = ddmmyyyy[3];
    return `${year}-${month}-${day}`;
  }

  const yyyymmdd = raw.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (yyyymmdd) {
    const year = yyyymmdd[1];
    const month = yyyymmdd[2].padStart(2, '0');
    const day = yyyymmdd[3].padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return '';
};

export default function RHIntegrationTrackingPage({ user, activeModule = 'b2b' }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [exits, setExits] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState('');
  const [selectedExitId, setSelectedExitId] = useState('');
  const [creatingIntegration, setCreatingIntegration] = useState(false);
  const [creatingExit, setCreatingExit] = useState(false);
  const [message, setMessage] = useState('');

  const [createIntegrationForm, setCreateIntegrationForm] = useState({
    user_id: '',
    client_type: 'amazon'
  });

  const [createExitForm, setCreateExitForm] = useState({
    user_id: '',
    exit_type: 'demission',
    comment: ''
  });

  const selectedIntegration = useMemo(
    () => integrations.find((item) => item.integration_id === selectedIntegrationId) || null,
    [integrations, selectedIntegrationId]
  );

  const selectedExit = useMemo(
    () => exits.find((item) => item.exit_id === selectedExitId) || null,
    [exits, selectedExitId]
  );

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [dashRes, usersRes, eligibleRes, integrationsRes, exitsRes] = await Promise.all([
        axios.get(`${API}/rh/dashboard`, { withCredentials: true }),
        axios.get(`${API}/rh/accounts/pending-validation`, { withCredentials: true }),
        axios.get(`${API}/rh/integration/eligible-users`, { withCredentials: true }),
        axios.get(`${API}/rh/integration`, { withCredentials: true }),
        axios.get(`${API}/rh/exit`, { withCredentials: true })
      ]);
      setDashboard(dashRes.data || null);
      setPendingAccounts(Array.isArray(usersRes.data) ? usersRes.data : []);
      setEligibleUsers(Array.isArray(eligibleRes.data) ? eligibleRes.data : []);
      setIntegrations(Array.isArray(integrationsRes.data) ? integrationsRes.data : []);
      setExits(Array.isArray(exitsRes.data) ? exitsRes.data : []);
    } catch (error) {
      setMessage(`Erreur chargement module RH: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const validatePendingAccount = async (userId) => {
    try {
      await axios.put(`${API}/rh/accounts/${userId}/validate`, {}, { withCredentials: true });
      setMessage('Compte validé. Il est maintenant éligible au lancement d’intégration.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur validation compte: ${error.response?.data?.detail || error.message}`);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const createIntegration = async () => {
    if (!createIntegrationForm.user_id) {
      setMessage('Veuillez sélectionner un compte salarié.');
      return;
    }
    setCreatingIntegration(true);
    try {
      await axios.post(
        `${API}/rh/integration`,
        {
          user_id: createIntegrationForm.user_id,
          client_type: createIntegrationForm.client_type
        },
        { withCredentials: true }
      );
      setMessage('Fiche d’intégration RH créée.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur création intégration: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreatingIntegration(false);
    }
  };

  const updateStep = async (stepKey, payload) => {
    if (!selectedIntegrationId) return;
    try {
      await axios.put(`${API}/rh/integration/${selectedIntegrationId}/step/${stepKey}`, payload, { withCredentials: true });
      setMessage('Étape mise à jour.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur étape: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateIntegrationFields = async (payload) => {
    if (!selectedIntegrationId) return;
    try {
      await axios.put(`${API}/rh/integration/${selectedIntegrationId}/fields`, payload, { withCredentials: true });
      setMessage('Champs RH mis à jour.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur mise à jour fiche: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateIntegrationLifecycle = async (lifecycleStatus) => {
    if (!selectedIntegrationId) return;
    try {
      await axios.put(`${API}/rh/integration/${selectedIntegrationId}/lifecycle`, { lifecycle_status: lifecycleStatus }, { withCredentials: true });
      setMessage('Cycle intégration mis à jour.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur cycle intégration: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createExit = async () => {
    if (!createExitForm.user_id) {
      setMessage('Veuillez sélectionner un salarié pour la sortie.');
      return;
    }
    setCreatingExit(true);
    try {
      await axios.post(`${API}/rh/exit`, createExitForm, { withCredentials: true });
      setMessage('Fiche de sortie RH créée.');
      setCreateExitForm((prev) => ({ ...prev, comment: '' }));
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur création sortie: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreatingExit(false);
    }
  };

  const updateExit = async (payload) => {
    if (!selectedExitId) return;
    try {
      await axios.put(`${API}/rh/exit/${selectedExitId}`, payload, { withCredentials: true });
      setMessage('Sortie RH mise à jour.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur mise à jour sortie: ${error.response?.data?.detail || error.message}`);
    }
  };

  const deactivateExitUser = async () => {
    if (!selectedExit?.user_id) return;
    try {
      const reasonMap = {
        demission: 'Démission',
        fin_periode_essai: 'Fin de période d\'essai (initiative employeur)',
        fin_contrat: 'Fin de contrat',
        abandon_poste: 'Abandon de poste',
        licenciement: 'Licenciement',
        rupture_conventionnelle: 'Rupture conventionnelle'
      };
      await axios.put(
        `${API}/users/${selectedExit.user_id}/deactivate`,
        { termination_reason: reasonMap[selectedExit.exit_type] || 'Fin de contrat' },
        { withCredentials: true }
      );
      await updateExit({ lifecycle_status: 'closed' });
      setMessage('Compte salarié désactivé et sortie clôturée.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur désactivation compte: ${error.response?.data?.detail || error.message}`);
    }
  };

  const archiveExitUser = async () => {
    if (!selectedExit?.user_id) return;
    try {
      await axios.put(`${API}/rh/employee/${selectedExit.user_id}/archive`, {}, { withCredentials: true });
      await updateExit({ lifecycle_status: 'archived' });
      setMessage('Salarié archivé et sortie marquée archivage.');
      await refreshAll();
    } catch (error) {
      setMessage(`Erreur archivage salarié: ${error.response?.data?.detail || error.message}`);
    }
  };

  const orderedSteps = useMemo(() => {
    if (!selectedIntegration) return [];
    const flow = RH_FLOW_CONFIG[selectedIntegration.client_type] || RH_FLOW_CONFIG.amazon;
    const steps = Array.isArray(selectedIntegration.steps) ? selectedIntegration.steps : [];
    const stepMap = new Map(steps.map((step) => [step.key, step]));

    return flow.map((definition) => {
      let resolvedKey = definition.key;
      let step = stepMap.get(definition.key);

      if (!step && Array.isArray(definition.aliases)) {
        const aliasMatch = definition.aliases.find((alias) => stepMap.has(alias));
        if (aliasMatch) {
          resolvedKey = aliasMatch;
          step = stepMap.get(aliasMatch);
        }
      }

      const fallbackDate =
        definition.key === 'projected_start_date'
          ? selectedIntegration.projected_start_date
          : definition.key === 'integration_completed'
            ? selectedIntegration.first_day_done_date
            : null;

      return {
        ...definition,
        resolvedKey,
        state: step?.state || 'not_started',
        date: normalizeRhDate(step?.date || fallbackDate || ''),
        trainer: step?.trainer || '',
        comment: step?.comment || ''
      };
    });
  }, [selectedIntegration]);

  const progressionStats = useMemo(() => {
    const total = orderedSteps.length;
    const completed = orderedSteps.filter((s) => s.state === 'completed').length;
    const inProgress = orderedSteps.find((s) => s.state === 'in_progress');
    return {
      total,
      completed,
      inProgressLabel: inProgress?.label || 'Aucune étape en cours'
    };
  }, [orderedSteps]);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="rh-integration-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="rh-integration-title">Suivi Intégration RH</h1>
          <p className="text-sm text-slate-600" data-testid="rh-integration-subtitle">
            Suivi centralisé intégration/sortie — accès complet uniquement.
          </p>
        </div>

        {message && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800" data-testid="rh-integration-feedback">
            {message}
          </div>
        )}

        <Card data-testid="rh-dashboard-card">
          <CardHeader>
            <CardTitle>Indicateurs RH</CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !dashboard ? (
              <p className="text-sm text-slate-500">Chargement…</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                  ['Comptes à valider', dashboard.accounts_to_validate],
                  ['Intégrations à lancer', dashboard.integrations_to_launch],
                  ['Intégrations en cours', dashboard.integrations_in_progress],
                  ['Intégrations terminées', dashboard.integrations_completed],
                  ['Contrats en attente', dashboard.contracts_pending],
                  ['Contrats non signés', dashboard.contracts_not_signed],
                  ['Formations en attente', dashboard.formations_pending],
                  ['Accurate en attente', dashboard.accurate_pending],
                  ['Premiers jours à venir', dashboard.first_days_upcoming],
                  ['Salariés prêts à activer', dashboard.employees_ready_to_activate],
                  ['Salariés actifs', dashboard.active_employees],
                  ['Démissions en cours', dashboard.demissions_open],
                  ['Fins PE en cours', dashboard.trial_ends_open],
                  ['Abandons de poste', dashboard.abandon_poste_open],
                  ['Comptes à désactiver', dashboard.accounts_to_disable]
                ].map(([label, value]) => (
                  <div key={label} className="rounded border bg-slate-50 p-3" data-testid={`rh-kpi-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-xl font-bold text-slate-900">{value ?? 0}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="integration" className="space-y-4" data-testid="rh-main-tabs">
          <TabsList>
            <TabsTrigger value="pending-accounts" data-testid="rh-tab-pending-accounts">Comptes à valider</TabsTrigger>
            <TabsTrigger value="integration" data-testid="rh-tab-integration">Suivi intégration</TabsTrigger>
            <TabsTrigger value="sortie" data-testid="rh-tab-sortie">Suivi sortie</TabsTrigger>
          </TabsList>

          <TabsContent value="pending-accounts" className="space-y-4">
            <Card data-testid="rh-pending-accounts-card">
              <CardHeader>
                <CardTitle>Comptes à valider</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingAccounts.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="rh-pending-accounts-empty">Aucun compte en attente de validation.</p>
                ) : (
                  <div className="space-y-2" data-testid="rh-pending-accounts-list">
                    {pendingAccounts.map((acc) => (
                      <div key={acc.user_id} className="rounded border border-slate-200 bg-white p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`rh-pending-account-${acc.user_id}`}>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{acc.name || acc.email}</p>
                          <p className="text-xs text-slate-600">{acc.email} · {acc.phone || 'Téléphone non renseigné'}</p>
                          <p className="text-xs text-slate-500">{acc.site_name || 'Sans site'} · {acc.rh_status}</p>
                        </div>
                        <Button onClick={() => validatePendingAccount(acc.user_id)} data-testid={`rh-validate-account-button-${acc.user_id}`}>
                          Valider le compte
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integration" className="space-y-4">
            <Card data-testid="rh-create-integration-card">
              <CardHeader>
                <CardTitle>Démarrer une intégration</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Compte salarié existant</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-3 py-2"
                    value={createIntegrationForm.user_id}
                    onChange={(e) => setCreateIntegrationForm((p) => ({ ...p, user_id: e.target.value }))}
                    data-testid="rh-create-integration-user-select"
                  >
                    <option value="">Sélectionner un salarié</option>
                    {eligibleUsers.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name} · {u.email} · {u.site_name || 'Sans site'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Type client</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-3 py-2"
                    value={createIntegrationForm.client_type}
                    onChange={(e) => setCreateIntegrationForm((p) => ({ ...p, client_type: e.target.value }))}
                    data-testid="rh-create-integration-client-type"
                  >
                    <option value="amazon">Amazon</option>
                    <option value="colis_prive">Colis Privé</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button onClick={createIntegration} disabled={creatingIntegration} data-testid="rh-create-integration-submit-button">
                    {creatingIntegration ? 'Création…' : 'Créer la fiche'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="rh-integration-list-card">
              <CardHeader>
                <CardTitle>Fiches intégration</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 border rounded-md p-2 max-h-[520px] overflow-auto" data-testid="rh-integration-list">
                  {integrations.map((item) => (
                    <button
                      key={item.integration_id}
                      className={`w-full text-left rounded border px-3 py-2 mb-2 ${selectedIntegrationId === item.integration_id ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-slate-200'}`}
                      onClick={() => setSelectedIntegrationId(item.integration_id)}
                      data-testid={`rh-integration-item-${item.integration_id}`}
                    >
                      <p className="text-sm font-semibold text-slate-900">{item.full_name || item.email || item.integration_id}</p>
                      <p className="text-xs text-slate-600">{item.client_type} · {item.site_name || 'Sans site'}</p>
                      <p className="text-xs text-slate-500">{item.lifecycle_status}</p>
                    </button>
                  ))}
                </div>

                <div className="lg:col-span-2 border rounded-md p-3" data-testid="rh-integration-details">
                  {!selectedIntegration ? (
                    <p className="text-sm text-slate-500">Sélectionnez une fiche.</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <p className="text-sm" data-testid="rh-details-account-sync">
                          <strong>Compte créé:</strong> {selectedIntegration.account_created ? 'Oui' : 'Non'}
                          {' · '}
                          <strong>Date:</strong> {selectedIntegration.account_created_at ? new Date(selectedIntegration.account_created_at).toLocaleDateString('fr-FR') : '—'}
                        </p>
                        <p className="text-sm" data-testid="rh-details-contact-sync">
                          <strong>Email:</strong> {selectedIntegration.email || '—'} · <strong>Téléphone:</strong> {selectedIntegration.phone || '—'}
                        </p>
                        <p className="text-sm" data-testid="rh-details-site-sync">
                          <strong>Site:</strong> {selectedIntegration.site_name || selectedIntegration.site_id || '—'}
                        </p>
                        <p className="text-sm" data-testid="rh-details-lifecycle-status">
                          <strong>Statut:</strong> {selectedIntegration.lifecycle_status}
                        </p>
                        <div>
                          <Label>Clôture / Archivage intégration</Label>
                          <select
                            className="w-full mt-1 border rounded-md px-3 py-2"
                            value={selectedIntegration.lifecycle_status || 'in_progress'}
                            onChange={(e) => updateIntegrationLifecycle(e.target.value)}
                            data-testid="rh-integration-lifecycle-select"
                          >
                            <option value="in_progress">En cours</option>
                            <option value="completed">Terminée</option>
                            <option value="closed">Clôturée</option>
                            <option value="archived">Archivée</option>
                          </select>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="rh-progress-summary-card">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <p className="text-sm font-semibold text-slate-900" data-testid="rh-progress-summary-title">
                            Progression {selectedIntegration.client_type === 'colis_prive' ? 'Colis Privé' : 'Amazon'}
                          </p>
                          <p className="text-sm text-slate-700" data-testid="rh-progress-summary-stats">
                            {progressionStats.completed}/{progressionStats.total} étapes terminées
                          </p>
                        </div>
                        <p className="text-xs text-slate-600 mt-1" data-testid="rh-progress-current-step">
                          Étape en cours: {progressionStats.inProgressLabel}
                        </p>
                      </div>

                      <div className="space-y-3" data-testid="rh-steps-list">
                        {orderedSteps.map((step, index) => (
                          <div key={`${step.key}-${step.resolvedKey}`} className="rounded border border-slate-200 p-3" data-testid={`rh-step-card-${step.key}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-slate-500" data-testid={`rh-step-order-${step.key}`}>{index + 1}.</span>
                              {getStatusIcon(step.state)}
                              <p className="text-sm font-semibold text-slate-900" data-testid={`rh-step-label-${step.key}`}>{step.label}</p>
                              {index < orderedSteps.length - 1 && <ArrowRight className="h-4 w-4 text-slate-400" />}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
                              <select
                                className="border rounded px-2 py-2 text-sm"
                                value={step.state || 'not_started'}
                                onChange={(e) => updateStep(step.resolvedKey, { state: e.target.value, date: normalizeRhDate(step.date) || null, trainer: step.trainer || null, comment: step.comment || null })}
                                data-testid={`rh-step-state-${step.key}`}
                              >
                                {STEP_STATE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>

                              <Input
                                type="date"
                                value={step.date || ''}
                                onChange={(e) => updateStep(step.resolvedKey, { state: step.state || 'not_started', date: normalizeRhDate(e.target.value) || null, trainer: step.trainer || null, comment: step.comment || null })}
                                data-testid={`rh-step-date-${step.key}`}
                              />

                              {step.training ? (
                                <Input
                                  value={step.trainer || ''}
                                  onChange={(e) => updateStep(step.resolvedKey, { state: step.state || 'not_started', date: normalizeRhDate(step.date) || null, trainer: e.target.value, comment: step.comment || null })}
                                  placeholder="Formateur"
                                  data-testid={`rh-step-trainer-${step.key}`}
                                />
                              ) : (
                                <div className="hidden md:block" data-testid={`rh-step-no-trainer-${step.key}`} />
                              )}

                              <Input
                                value={step.comment || ''}
                                onChange={(e) => updateStep(step.resolvedKey, { state: step.state || 'not_started', date: normalizeRhDate(step.date) || null, trainer: step.trainer || null, comment: e.target.value })}
                                placeholder="Commentaire"
                                data-testid={`rh-step-comment-${step.key}`}
                              />
                            </div>
                          </div>
                        ))}

                        <div className="rounded border border-slate-200 p-3" data-testid="rh-integration-global-comment-card">
                          <Label>Commentaire RH global</Label>
                          <Input
                            value={selectedIntegration.comment || ''}
                            onChange={(e) => updateIntegrationFields({ comment: e.target.value })}
                            placeholder="Commentaire RH"
                            data-testid="rh-field-global-comment"
                          />
                        </div>
                      </div>

                      <div className="border-t pt-3" data-testid="rh-integration-history">
                        <p className="text-sm font-semibold mb-2">Historique</p>
                        <div className="space-y-2 max-h-56 overflow-auto">
                          {(selectedIntegration.history || []).slice().reverse().map((h, idx) => (
                            <div key={`${h.timestamp}-${idx}`} className="rounded border bg-slate-50 p-2 text-xs" data-testid={`rh-history-entry-${idx}`}>
                              <p>{h.date} - {h.time}</p>
                              <p className="font-medium">{h.actor_name || h.actor_email || '—'}</p>
                              <p>{h.field}: {JSON.stringify(h.old_value)} → {JSON.stringify(h.new_value)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sortie" className="space-y-4">
            <Card data-testid="rh-create-exit-card">
              <CardHeader>
                <CardTitle>Déclarer une sortie</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label>Salarié</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-3 py-2"
                    value={createExitForm.user_id}
                    onChange={(e) => setCreateExitForm((p) => ({ ...p, user_id: e.target.value }))}
                    data-testid="rh-create-exit-user-select"
                  >
                    <option value="">Sélectionner un salarié</option>
                    {eligibleUsers.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name} · {u.site_name || 'Sans site'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Statut sortie</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-3 py-2"
                    value={createExitForm.exit_type}
                    onChange={(e) => setCreateExitForm((p) => ({ ...p, exit_type: e.target.value }))}
                    data-testid="rh-create-exit-type-select"
                  >
                    {EXIT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Commentaire</Label>
                  <Input
                    value={createExitForm.comment}
                    onChange={(e) => setCreateExitForm((p) => ({ ...p, comment: e.target.value }))}
                    placeholder="Commentaire RH"
                    data-testid="rh-create-exit-comment-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={createExit} disabled={creatingExit} data-testid="rh-create-exit-submit-button">
                    {creatingExit ? 'Création…' : 'Créer sortie'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="rh-exit-list-card">
              <CardHeader>
                <CardTitle>Suivi des sorties</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 border rounded-md p-2 max-h-[520px] overflow-auto" data-testid="rh-exit-list">
                  {exits.map((item) => (
                    <button
                      key={item.exit_id}
                      className={`w-full text-left rounded border px-3 py-2 mb-2 ${selectedExitId === item.exit_id ? 'bg-rose-50 border-rose-300' : 'bg-white border-slate-200'}`}
                      onClick={() => setSelectedExitId(item.exit_id)}
                      data-testid={`rh-exit-item-${item.exit_id}`}
                    >
                      <p className="text-sm font-semibold text-slate-900">{item.user_name}</p>
                      <p className="text-xs text-slate-600">{item.exit_type}</p>
                      <p className="text-xs text-slate-500">{item.lifecycle_status}</p>
                    </button>
                  ))}
                </div>

                <div className="lg:col-span-2 border rounded-md p-3" data-testid="rh-exit-details">
                  {!selectedExit ? (
                    <p className="text-sm text-slate-500">Sélectionnez une sortie.</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label>Statut sortie</Label>
                          <select
                            className="w-full mt-1 border rounded-md px-3 py-2"
                            value={selectedExit.exit_type}
                            onChange={(e) => updateExit({ exit_type: e.target.value })}
                            data-testid="rh-exit-type-update"
                          >
                            {EXIT_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>État dossier</Label>
                          <select
                            className="w-full mt-1 border rounded-md px-3 py-2"
                            value={selectedExit.lifecycle_status || 'open'}
                            onChange={(e) => updateExit({ lifecycle_status: e.target.value })}
                            data-testid="rh-exit-lifecycle-update"
                          >
                            <option value="open">Ouvert</option>
                            <option value="closed">Clôturé</option>
                            <option value="archived">Archivé</option>
                          </select>
                        </div>
                        <div>
                          <Label>Commentaire</Label>
                          <Input
                            value={selectedExit.comment || ''}
                            onChange={(e) => updateExit({ comment: e.target.value })}
                            data-testid="rh-exit-comment-update"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2" data-testid="rh-exit-actions-row">
                        <Button variant="outline" onClick={() => updateExit({ lifecycle_status: 'closed' })} data-testid="rh-exit-close-button">
                          Clôturer sortie
                        </Button>
                        <Button variant="outline" onClick={() => updateExit({ lifecycle_status: 'archived' })} data-testid="rh-exit-archive-button">
                          Archiver sortie
                        </Button>
                        <Button variant="destructive" onClick={deactivateExitUser} data-testid="rh-exit-deactivate-account-button">
                          Désactiver le compte salarié
                        </Button>
                        <Button variant="outline" onClick={archiveExitUser} data-testid="rh-exit-archive-employee-button">
                          Archiver le salarié
                        </Button>
                      </div>

                      <div className="border-t pt-3" data-testid="rh-exit-history">
                        <p className="text-sm font-semibold mb-2">Historique</p>
                        <div className="space-y-2 max-h-56 overflow-auto">
                          {(selectedExit.history || []).slice().reverse().map((h, idx) => (
                            <div key={`${h.timestamp}-${idx}`} className="rounded border bg-slate-50 p-2 text-xs" data-testid={`rh-exit-history-entry-${idx}`}>
                              <p>{h.date} - {h.time}</p>
                              <p className="font-medium">{h.actor_name || h.actor_email || '—'}</p>
                              <p>{h.field}: {JSON.stringify(h.old_value)} → {JSON.stringify(h.new_value)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
